/**
 * Admin Leave Management Component
 * Review and approve/reject leave requests
 */
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { leaveAPI } from '@/lib/api';
import { 
  Calendar, Clock, CheckCircle, XCircle, Loader2, 
  CalendarDays, User, Filter
} from 'lucide-react';

const LEAVE_TYPES = [
  { value: 'annual', label: 'Annual Leave', color: 'bg-blue-100 text-blue-700' },
  { value: 'sick', label: 'Sick Leave', color: 'bg-red-100 text-red-700' },
  { value: 'personal', label: 'Personal Leave', color: 'bg-purple-100 text-purple-700' },
  { value: 'unpaid', label: 'Unpaid Leave', color: 'bg-gray-100 text-gray-700' }
];

export const AdminLeaveManagement = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    fetchLeaveRequests();
  }, [statusFilter]);

  const fetchLeaveRequests = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const res = await leaveAPI.getAll(params);
      setRequests(res.data.requests || []);
      setSummary(res.data.summary || {});
    } catch (error) {
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = (request) => {
    setSelectedRequest(request);
    setReviewNotes('');
    setShowReviewDialog(true);
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedRequest) return;
    
    setReviewing(true);
    try {
      await leaveAPI.update(selectedRequest.id, {
        status,
        admin_notes: reviewNotes || null
      });
      toast.success(`Leave request ${status} successfully!`);
      setShowReviewDialog(false);
      setSelectedRequest(null);
      fetchLeaveRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update request');
    } finally {
      setReviewing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300',
      approved: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
      cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    };
    return styles[status] || styles.pending;
  };

  const getLeaveTypeBadge = (type) => {
    const leaveType = LEAVE_TYPES.find(t => t.value === type);
    return leaveType?.color || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { 
        day: 'numeric', month: 'short', year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Summary */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-5 text-white">
        <div className="flex items-center gap-3 mb-2">
          <CalendarDays className="w-7 h-7" />
          <h2 className="text-xl font-bold">Leave Management</h2>
        </div>
        <p className="text-purple-100 text-sm">Review and manage employee leave requests</p>
        
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-purple-400/30">
          <div className="text-center">
            <p className="text-2xl font-bold">{summary.total_requests || 0}</p>
            <p className="text-xs text-purple-200">Total</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-300">{summary.pending || 0}</p>
            <p className="text-xs text-purple-200">Pending</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-300">{summary.approved || 0}</p>
            <p className="text-xs text-purple-200">Approved</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-300">{summary.rejected || 0}</p>
            <p className="text-xs text-purple-200">Rejected</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4">
        <div className="flex items-center gap-4">
          <Filter className="w-4 h-4 text-gray-500" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="leave-status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Leave Requests List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : requests.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-8 text-center">
          <CalendarDays className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">No Leave Requests</h3>
          <p className="text-gray-500 dark:text-gray-400">No leave requests found for the selected filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(request => (
            <div 
              key={request.id}
              className={`bg-white dark:bg-slate-800 border rounded-xl p-4 transition-colors ${
                request.status === 'pending' 
                  ? 'border-yellow-300 dark:border-yellow-700' 
                  : 'border-gray-200 dark:border-slate-700'
              }`}
              data-testid={`admin-leave-request-${request.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1">
                  {/* Employee Info */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                      <User className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">{request.user_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{request.user_email}</p>
                    </div>
                  </div>
                  
                  {/* Badges */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <Badge className={getLeaveTypeBadge(request.leave_type)}>
                      {LEAVE_TYPES.find(t => t.value === request.leave_type)?.label || request.leave_type}
                    </Badge>
                    <Badge className={getStatusBadge(request.status)}>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </Badge>
                    {request.half_day && (
                      <Badge className="bg-orange-100 text-orange-700">
                        Half Day ({request.half_day_period})
                      </Badge>
                    )}
                  </div>
                  
                  {/* Dates */}
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(request.start_date)}
                      {request.start_date !== request.end_date && ` - ${formatDate(request.end_date)}`}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {request.days_requested} day{request.days_requested !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  {/* Reason */}
                  <div className="mt-2 p-2 bg-gray-50 dark:bg-slate-700 rounded">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>Reason:</strong> {request.reason}
                    </p>
                  </div>
                  
                  {/* Admin Notes (if exists) */}
                  {request.admin_notes && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        <strong>Admin Notes:</strong> {request.admin_notes}
                      </p>
                    </div>
                  )}
                  
                  {/* Approval Info */}
                  {request.approved_by_name && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {request.status === 'approved' ? 'Approved' : 'Reviewed'} by {request.approved_by_name} on {formatDate(request.approved_at)}
                    </p>
                  )}
                </div>
                
                {/* Actions */}
                {request.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleReview(request)}
                      className="bg-teal-600 text-white hover:bg-teal-700"
                      data-testid={`review-leave-${request.id}`}
                    >
                      Review
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="bg-white dark:bg-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white">Review Leave Request</DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4 mt-4">
              {/* Request Summary */}
              <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-lg space-y-2">
                <p className="font-medium text-gray-800 dark:text-white">{selectedRequest.user_name}</p>
                <div className="flex items-center gap-2">
                  <Badge className={getLeaveTypeBadge(selectedRequest.leave_type)}>
                    {LEAVE_TYPES.find(t => t.value === selectedRequest.leave_type)?.label}
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedRequest.days_requested} day(s)
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {formatDate(selectedRequest.start_date)}
                  {selectedRequest.start_date !== selectedRequest.end_date && ` - ${formatDate(selectedRequest.end_date)}`}
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{selectedRequest.reason}</p>
              </div>
              
              {/* Admin Notes */}
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add any notes for the employee..."
                  className="min-h-[80px]"
                />
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => handleUpdateStatus('approved')}
                  disabled={reviewing}
                  className="flex-1 bg-green-600 text-white hover:bg-green-700"
                  data-testid="approve-leave-btn"
                >
                  {reviewing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Approve
                </Button>
                <Button
                  onClick={() => handleUpdateStatus('rejected')}
                  disabled={reviewing}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="reject-leave-btn"
                >
                  {reviewing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-2" />
                  )}
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLeaveManagement;
